import React, { createContext, useContext } from "react";

/**
 * Customer-facing i18n. Minimum-safe implementation:
 * - Two dictionaries: en, ar (Arabic values are hand-crafted, not machine).
 * - `LangContext` defaults to English so any component that uses `useLang()`
 *   OUTSIDE a provider (e.g. the technician page which mounts the same
 *   VoiceRecorder / VoiceBubble) stays in English.
 * - Persistence via localStorage under `idea_customer_language`.
 * - Technical strings (order number, component model names, SKUs, timer
 *   digits) are NEVER translated — the customer sees them verbatim.
 */

export const TRANSLATIONS = {
  en: {
    private_session: "Private Build Session",
    secure_connection: "SECURE CONNECTION",
    secure_session: "SECURE SESSION",
    status_live: "LIVE",
    status_completed: "SESSION CLOSED",
    status_waiting: "WAITING",
    status_paused: "PAUSED",
    step_waiting: "Waiting for\nTechnician",
    step_live: "Live\nAssembly",
    step_completed: "Assembly\nCompleted",
    step_testing: "Next:\nTesting",
    your_build: "Your Build",
    footer_secure_title: "SECURE SESSION",
    footer_secure_desc: "Your link is private and unique to your order.",
    footer_private_title: "100% PRIVATE",
    footer_private_desc: "Only you can watch this build.",
    footer_help_title: "NEED HELP?",
    footer_help_desc: "Contact our support team anytime.",
    footer_tagline: "BUILT DIFFERENT",
    build_completed: "BUILD COMPLETED",
    loading: "Loading…",
    link_invalid: "This link is not valid.",
    link_load_error: "Could not load live view.",
    link_help: "If you believe this is a mistake, please contact IDEA support.",
    headline_waiting_prefix: "Your PC will be built ",
    headline_waiting_highlight: "very soon.",
    headline_paused_prefix: "Build temporarily ",
    headline_paused_highlight: "paused.",
    headline_live_prefix: "Your PC is being built ",
    headline_live_highlight: "live.",
    headline_testing_prefix: "Ready for ",
    headline_testing_highlight: "testing.",
    headline_completed_prefix: "Your PC is ",
    headline_completed_highlight: "ready.",
    label_order: "Order",
    label_station: "Station",
    label_technician: "Technician",
    label_build_progress: "Build Progress",
    label_build_time: "Build Time",
    timer_waiting: "Timer will start at build begin",
    timer_live: "Live — running now",
    timer_completed: "Final assembly time",
    // Chat
    chat_title: "TALK TO YOUR TECHNICIAN",
    chat_session_ended: "This build session has ended.",
    chat_will_open: "Chat will open when your build starts.",
    chat_send_note: "Send a note or question directly to your technician.",
    chat_reconnecting: "reconnecting…",
    chat_no_messages: "No messages yet",
    chat_type_message: "Type your message…",
    chat_placeholder_ended: "Session ended",
    chat_placeholder_waiting: "Chat will open when your build starts",
    chat_send: "SEND",
    // Rating
    rating_thanks_title: "Thank you for your feedback.",
    rating_thanks_desc_prefix: "We shared it with ",
    rating_default_name: "your technician",
    rating_prompt: "How was your build experience?",
    rating_professionalism: "Professionalism",
    rating_communication: "Communication",
    rating_build_quality: "Build Quality",
    rating_overall: "Overall Experience",
    rating_comment_prompt: "Tell us more about your experience",
    rating_optional: "(optional)",
    rating_comment_placeholder: "Share your thoughts...",
    rating_rate_all: "Rate all four",
    rating_submit: "SUBMIT RATING",
    rating_submitting: "SUBMITTING…",
    rating_submitted_toast: "Rating submitted",
    rating_already_toast: "Rating already submitted",
    rating_error_toast: "Could not submit rating. Please try again.",
    score_excellent: "EXCELLENT",
    score_great: "GREAT",
    score_good: "GOOD",
    score_could: "COULD BE BETTER",
    score_needs: "NEEDS IMPROVEMENT",
    // Voice
    voice_record_tt: "Record a voice message (max 60s)",
    voice_not_supported_tt: "Voice not supported here",
    voice_cancel_tt: "Cancel",
    voice_stop_send_tt: "Stop and send",
    voice_send_tt: "Send",
    voice_not_supported_toast: "Voice messages aren't supported in this browser.",
    voice_hold_title: "Hold to record",
    voice_hold_desc: "Recording was too short. Try again — at least 1 second.",
    voice_failed: "Recording failed. Try again.",
    voice_perm_denied: "Microphone permission denied.",
    voice_no_mic: "No microphone found.",
    voice_no_access: "Could not access microphone.",
    voice_error: "Recording error. Please try again.",
    voice_play_tt: "Play voice message",
    voice_pause_tt: "Pause voice message",
    voice_unavailable: "unavailable",
    player_waiting: "WAITING FOR THE TECHNICIAN",
    player_initializing: "Live feed initializing…",
    player_will_start: "The stream will start when the technician begins your build.",
    player_preparing: "Preparing the live stream…",
    testing_title: "Testing & Quality Check",
    testing_subtitle: "Watch your PC being tested live.",
    testing_not_started: "Testing has not started yet.",
    testing_step_windows: "Windows Installation",
    testing_step_gpu_driver: "GPU Driver Installation",
    testing_step_bench: "GPU & CPU Bench Test",
    testing_status_pending: "PENDING",
    testing_status_installing: "INSTALLING…",
    testing_status_testing: "TESTING…",
    testing_status_completed: "COMPLETED ✓",
    testing_temp_gpu: "GPU Temperature",
    testing_temp_cpu: "CPU Temperature",
    testing_expected_range: "Expected Range",
    testing_waiting_result: "Waiting for test result…",
    testing_class_excellent: "EXCELLENT TEMP",
    testing_class_good: "GOOD TEMP",
    testing_class_high: "HIGH TEMP",
    testing_class_too_high: "TEMP TOO HIGH",
  },
  ar: {
    private_session: "جلسة تجميع خاصة",
    secure_connection: "اتصال آمن",
    secure_session: "جلسة آمنة",
    status_live: "مباشر",
    status_completed: "انتهت الجلسة",
    status_waiting: "بانتظار البدء",
    status_paused: "متوقف مؤقتاً",
    step_waiting: "بانتظار\nالفني",
    step_live: "التجميع\nمباشر",
    step_completed: "اكتمل\nالتجميع",
    step_testing: "التالي:\nالاختبار",
    your_build: "تجميعتك",
    footer_secure_title: "جلسة آمنة",
    footer_secure_desc: "الرابط خاص وفريد لطلبك.",
    footer_private_title: "خاص 100%",
    footer_private_desc: "أنت فقط تستطيع مشاهدة هذا التجميع.",
    footer_help_title: "تحتاج مساعدة؟",
    footer_help_desc: "تواصل مع فريق الدعم في أي وقت.",
    footer_tagline: "تجميع مختلف",
    build_completed: "اكتمل التجميع",
    testing_title: "الاختبار وفحص الجودة",
    testing_subtitle: "شاهد اختبار جهازك مباشرة.",
    testing_not_started: "لم يبدأ الاختبار بعد.",
    testing_step_windows: "تثبيت ويندوز",
    testing_step_gpu_driver: "تثبيت تعريفات كرت الشاشة",
    testing_step_bench: "اختبار أداء المعالج وكرت الشاشة",
    testing_status_pending: "قيد الانتظار",
    testing_status_installing: "جارٍ التثبيت…",
    testing_status_testing: "جارٍ الاختبار…",
    testing_status_completed: "مكتمل ✓",
    testing_temp_gpu: "حرارة كرت الشاشة",
    testing_temp_cpu: "حرارة المعالج",
    testing_expected_range: "النطاق المتوقع",
    testing_waiting_result: "بانتظار نتيجة الاختبار…",
    testing_class_excellent: "حرارة ممتازة",
    testing_class_good: "حرارة جيدة",
    testing_class_high: "حرارة مرتفعة",
    testing_class_too_high: "الحرارة مرتفعة جدًا",
    loading: "جارٍ التحميل...",
    link_invalid: "هذا الرابط غير صالح.",
    link_load_error: "تعذر تحميل البث المباشر.",
    link_help: "إذا كنت تعتقد أن هذا خطأ، يرجى التواصل مع دعم IDEA.",
    headline_waiting_prefix: "سيبدأ تجميع جهازك ",
    headline_waiting_highlight: "قريبًا جدًا.",
    headline_paused_prefix: "التجميع ",
    headline_paused_highlight: "متوقف مؤقتاً.",
    headline_live_prefix: "جاري تجميع جهازك ",
    headline_live_highlight: "مباشرة.",
    headline_testing_prefix: "جاهز ",
    headline_testing_highlight: "للاختبار.",
    headline_completed_prefix: "جهازك ",
    headline_completed_highlight: "جاهز.",
    label_order: "رقم الطلب",
    label_station: "المحطة",
    label_technician: "الفني",
    label_build_progress: "تقدم التجميع",
    label_build_time: "مدة التجميع",
    timer_waiting: "سيبدأ العدّاد عند بدء التجميع",
    timer_live: "مباشر — يعمل الآن",
    timer_completed: "المدة النهائية للتجميع",
    // Chat
    chat_title: "تواصل مع الفني",
    chat_session_ended: "انتهت جلسة التجميع.",
    chat_will_open: "ستُفتح المحادثة عند بدء التجميع.",
    chat_send_note: "أرسل ملاحظتك أو سؤالك مباشرة إلى الفني.",
    chat_reconnecting: "جارٍ إعادة الاتصال…",
    chat_no_messages: "لا توجد رسائل بعد",
    chat_type_message: "اكتب رسالة...",
    chat_placeholder_ended: "انتهت الجلسة",
    chat_placeholder_waiting: "ستُفتح المحادثة عند بدء التجميع",
    chat_send: "إرسال",
    // Rating
    rating_thanks_title: "شكرًا على تقييمك.",
    rating_thanks_desc_prefix: "شاركنا تقييمك مع ",
    rating_default_name: "الفني",
    rating_prompt: "كيف كانت تجربة التجميع؟",
    rating_professionalism: "الاحترافية",
    rating_communication: "التواصل",
    rating_build_quality: "جودة التجميع",
    rating_overall: "التجربة العامة",
    rating_comment_prompt: "شاركنا مزيدًا من التفاصيل عن تجربتك",
    rating_optional: "(اختياري)",
    rating_comment_placeholder: "شاركنا رأيك...",
    rating_rate_all: "قيّم جميع الفئات",
    rating_submit: "إرسال التقييم",
    rating_submitting: "جارٍ الإرسال...",
    rating_submitted_toast: "تم إرسال التقييم",
    rating_already_toast: "تم إرسال التقييم مسبقًا",
    rating_error_toast: "تعذر إرسال التقييم. الرجاء المحاولة مرة أخرى.",
    score_excellent: "ممتاز",
    score_great: "رائع",
    score_good: "جيد",
    score_could: "يمكن أن يكون أفضل",
    score_needs: "بحاجة للتحسين",
    // Voice
    voice_record_tt: "تسجيل رسالة صوتية (بحد أقصى 60 ثانية)",
    voice_not_supported_tt: "الصوت غير مدعوم",
    voice_cancel_tt: "إلغاء",
    voice_stop_send_tt: "إيقاف وإرسال",
    voice_send_tt: "إرسال",
    voice_not_supported_toast: "الرسائل الصوتية غير مدعومة في هذا المتصفح.",
    voice_hold_title: "اضغط للتسجيل",
    voice_hold_desc: "التسجيل قصير جدًا. حاول مجددًا — ثانية واحدة على الأقل.",
    voice_failed: "فشل التسجيل. حاول مجددًا.",
    voice_perm_denied: "تم رفض إذن الميكروفون.",
    voice_no_mic: "لم يتم العثور على ميكروفون.",
    voice_no_access: "تعذر الوصول إلى الميكروفون.",
    voice_error: "خطأ في التسجيل. الرجاء المحاولة مجددًا.",
    voice_play_tt: "تشغيل الرسالة الصوتية",
    voice_pause_tt: "إيقاف الرسالة",
    voice_unavailable: "غير متاح",
    player_waiting: "بانتظار الفني",
    player_initializing: "جارٍ تجهيز البث المباشر…",
    player_will_start: "سيبدأ البث عندما يبدأ الفني بتجميع جهازك.",
    player_preparing: "جارٍ تجهيز البث…",
  },
};

function makeT(lang) {
  const dict = lang === "AR" ? TRANSLATIONS.ar : TRANSLATIONS.en;
  return (key) => (dict[key] ?? TRANSLATIONS.en[key] ?? key);
}

// Default context = English → components mounted OUTSIDE the customer provider
// (e.g. the technician page which reuses VoiceRecorder / VoiceBubble) stay in
// English without any code change on the technician side.
const DEFAULT_LANG = "EN";
const DEFAULT_T = makeT(DEFAULT_LANG);
export const LangContext = createContext({ lang: DEFAULT_LANG, t: DEFAULT_T });

export function useLang() {
  return useContext(LangContext);
}

const STORAGE_KEY = "idea_customer_language";

export function readStoredLang() {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v === "AR" ? "AR" : "EN";
  } catch { return "EN"; }
}

export function writeStoredLang(v) {
  try { localStorage.setItem(STORAGE_KEY, v === "AR" ? "AR" : "EN"); } catch {}
}

export function LangProvider({ lang, children }) {
  const value = React.useMemo(() => ({ lang, t: makeT(lang) }), [lang]);
  return <LangContext.Provider value={value}>{children}</LangContext.Provider>;
}
